import {
  hotelRatingBandBounds,
  mergeHotelOffers,
  type HotelOfferPagePhase,
  type HotelResultsFilterValue,
  type HotelResultsOrder,
} from '@voll-intelligence/intelligence-ui-kit';

import type { ChatUiHotelOffer, HotelResultsCuts } from '@voll-intelligence/genui-contract';

/** Nota máxima da escala (a mesma do card). O corte de nota vai como FAIXA
 *  fechada, não como `{from}` solto: o critério do fornecedor é
 *  `{$gte: from, $lte: to}`, e um `to` ausente viraria `$lte: null` no Mongo
 *  deles — o filtro passaria a casar nada em vez de casar tudo acima do piso. */
const RATING_SCALE_MAX = 5;

/**
 * Os eixos do painel traduzidos para o que o fornecedor aplica no conjunto
 * inteiro. Fora daqui, e cada um por um motivo distinto:
 *
 * - `amenityIds`: a hotels-api não indexa comodidade no cache da busca.
 * - café da manhã / reembolsável: a `SearchHotelFilter` deles declara
 *   `onlyBreakfast`/`onlyRefundable` e NENHUM caminho os lê (o corte real vive
 *   em `policies.onlyRateWithBreakfast`, que é a política da empresa, não a
 *   preferência do viajante).
 *
 * Os três sobrevivem como filtro de cliente em `matchesStayPreferences` e no
 * filtro de comodidade do painel — sobre o que já carregou, que é o teto
 * honesto enquanto o fornecedor não cortar.
 *
 * O acordo (#6928) é lido de verdade pelo fornecedor (`onlyAgreedRate`, até o
 * Apollo), então corta o conjunto inteiro, não só a página carregada.
 */
export function toHotelResultsCuts(filters: HotelResultsFilterValue): HotelResultsCuts | undefined {
  const cuts: HotelResultsCuts = {
    ...(filters.policyOnly ? { onlyPolicyWithIn: true } : {}),
    ...(filters.agreedRateOnly === true ? { onlyAgreedRate: true } : {}),
    ...(filters.maximumNightlyPrice === undefined ? {} : { maxPrice: filters.maximumNightlyPrice }),
    // Uma faixa POR banda marcada (#2263, união no fornecedor), geometria
    // derivada da fonte única do painel (`hotelRatingBandBounds`) — nunca de
    // literais próprios. `to` do fornecedor é INCLUSIVO (`$lte`), então bandas
    // adjacentes se tocam no limite: sobreposição inofensiva numa união, e na
    // banda solta a direção segura (o servidor manda a mais; o corte de cliente,
    // meio-aberto, é quem decide a lista visível).
    ...(filters.ratingBands === undefined || filters.ratingBands.length === 0
      ? {}
      : {
          rating: filters.ratingBands.map((floor) => {
            const { from, to } = hotelRatingBandBounds(floor);
            return { from, to: to ?? RATING_SCALE_MAX };
          }),
        }),
    ...(filters.maximumDistance === undefined ? {} : { restrictRadiusTo: filters.maximumDistance }),
  };
  return Object.keys(cuts).length > 0 ? cuts : undefined;
}

/** A ordem do painel no vocabulário do fornecedor (`HOTEL_SEARCH_ORDER`).
 *  `recommended` vira `DEFAULT` porque o recomendado já sobe primeiro em TODOS
 *  os sorts deles; `stars` é a NOTA (0–5) que a lista mostra, então `RATING`. */
export function toHotelResultsOrder(order: HotelResultsOrder): string {
  switch (order) {
    case 'price':
      return 'PRICE';
    case 'distance':
      return 'DISTANCE';
    case 'reviews':
      return 'RATING_COUNT';
    case 'stars':
      return 'RATING';
    case 'recommended':
      return 'DEFAULT';
    default: {
      const exhaustive: never = order;
      return exhaustive;
    }
  }
}

/**
 * Café da manhã e reembolsável pedidos na BUSCA, aplicados aqui porque o
 * fornecedor os ignora. Lê as marcas de nível de oferta, que descrevem o quarto
 * MAIS BARATO do hotel: um hotel com café numa diária mais cara sai da lista.
 * É o preço de não deixar o card prometer um corte que ninguém aplica; o
 * conserto de verdade é a hotels-api ler o filtro.
 *
 * Marca ausente não elimina: bloco antigo (replay) e oferta sem resume não
 * carregam o campo, e sumir com elas seria pior que mostrá-las.
 */
export function matchesStayPreferences(
  offer: ChatUiHotelOffer,
  preferences: { onlyBreakfast?: boolean; onlyRefundable?: boolean },
): boolean {
  if (preferences.onlyBreakfast && offer.breakfast === false) return false;
  if (preferences.onlyRefundable && offer.refundable === false) return false;
  return true;
}

/**
 * De onde a lista do painel sai neste render.
 *
 * Enquanto a paginação não RESPONDEU (`committed`), é o que o bloco trouxe — a
 * lista resumida da busca, que é o paint rápido. Depois é a página, SEMPRE,
 * inclusive vazia e inclusive depois de falhar.
 *
 * SEM corte no servidor, a página ESTENDE o bloco em vez de trocá-lo (#3727): o
 * bloco fica onde está e a página só acrescenta o que ele não tinha. A lista
 * cheia contém o bloco (é o mesmo cache no fornecedor), mas a PÁGINA 1 dela
 * não: a ordem do fornecedor põe à frente os hotéis dentro da política mais
 * perto do pino, e as sementes do bloco caem em páginas seguintes. Trocar
 * apagava da tela, em SP, as redes que o viajante já estava lendo. É a mesma
 * regra do inventário da sessão no servidor (`mergeHotelInventory`). COM corte
 * a página é o subconjunto pedido, e o bloco de fora do corte não entra.
 *
 * Falha SOB CORTE entra nessa regra de propósito: cair de volta no bloco
 * mostraria as ofertas originais, sem corte nenhum, como se fossem o resultado
 * do filtro. Com a página vazia no lugar, quem desenha decide — e o painel
 * desenha o aviso de leitura falhada, não uma lista vazia que diria "nenhum
 * hotel combina".
 *
 * SEM corte no servidor a conta é outra, e era o buraco: a lista do bloco são
 * hotéis reais da MESMA busca, então apagá-los troca "8 hotéis + aviso de que
 * falta carregar o resto" por "0 hotéis" — a tela perde resultado que já estava
 * em cena por causa de uma leitura de fundo. Medido no develop: 8 hotéis viraram
 * `0 hotéis · Não foi possível buscar hotéis`. Ordem e comodidade não contam
 * como corte aqui: as duas são aplicadas no cliente, sobre qualquer lista que
 * esteja em cena.
 *
 * `committed` e não a fase: trocar de corte devolve a fase para `loading`, e
 * ler a fase faria o painel piscar a lista resumida no meio da navegação.
 */
export function panelSourceOffers(
  page: { committed: boolean; offers: readonly ChatUiHotelOffer[]; phase: HotelOfferPagePhase },
  blockOffers: readonly ChatUiHotelOffer[],
  /** Há corte de SERVIDOR ativo? (`toHotelResultsCuts` devolveu algo.) */
  cutsActive = false,
): { paged: boolean; offers: readonly ChatUiHotelOffer[] } {
  if (!page.committed || (page.phase === 'error' && page.offers.length === 0 && !cutsActive)) {
    return { paged: false, offers: blockOffers };
  }
  return {
    paged: true,
    offers: cutsActive ? page.offers : mergeHotelOffers(blockOffers, page.offers),
  };
}

/**
 * O total do fornecedor só pode ser mostrado enquanto ele mede o MESMO conjunto
 * que a lista. Café da manhã e reembolsável são cortados aqui no cliente — o
 * fornecedor não os aplica nem os conta —, então com um deles ativo o "12 de
 * 57" viraria uma promessa de 45 hotéis que nenhum scroll alcança.
 *
 * Sem total, o cabeçalho conta o que está carregado, que é verdade sempre. O
 * filtro de comodidade não entra nessa regra: ele é um controle VISÍVEL do
 * painel, e "12 de 57" ali se lê como "12 passaram no seu filtro".
 */
export function panelTotal(
  total: number | undefined,
  preferences: { onlyBreakfast?: boolean; onlyRefundable?: boolean } | undefined,
  loaded = 0,
): number | undefined {
  const filtersLocally =
    Boolean(preferences?.onlyBreakfast) || Boolean(preferences?.onlyRefundable);
  if (filtersLocally) return undefined;
  // Total MENOR que o que já está em cena é contagem velha, não conjunto
  // pequeno: o fornecedor conta a lista resumida enquanto monta a completa, e a
  // primeira página já volta com 20 dela. Medido na busca real: página de 20 com
  // `total: 8`, e o cabeçalho dizia "20 hotéis de 8". Sem total ele conta o que
  // está carregado, que é verdade sempre — e a sentinela segue convidando.
  if (total !== undefined && total < loaded) return undefined;
  return total;
}
